import { useEffect } from 'react'
import type {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'
import { BogoBanner, BogoPromoModal } from '@mc/bogo'
import { EnterpriseAnnouncementBanner } from '@mc/enterprise-announcement'
import { TrialNoLongerAvailableModal } from '@mc/free-trial'
import { getBucket } from '@mc/next-growthbook'
import { useParsePromoFeatureVariables } from '@mc/promo-utils'
import { PAGE_SOURCES, PromoExplainerModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS, trackPage } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import ProfitWell from 'components/Head/ProfitWell'
import { GoogleOneTap } from 'experiments/pen-3951_v2_google_email_capture/components/GoogleOneTap'
import {
  CAROUSEL_SPRINGBOARD_EXP_FLAG,
  getCarouselSpringboardItems,
  useTrackCarouselSpringboardExperiment,
} from 'experiments/pen-5453_v1_hp_carousel_to_quiz'
import { QuizEntryModalLoader } from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/client'
import {
  getModalFeaturedClasses,
  QUIZ_ENTRY_IN_STICKY_FOOTER_EXP_FLAG,
} from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/server'
import {
  HeroQuizExpWrapper,
  BelowTheFoldExpWrapper,
  HP_INNOVATIVE_SWING_FLAG_NAME,
  StickyFooterExpWrapper,
} from 'experiments/pen-5690_v1_hp_innovative_swing'
import { trackProductViewed } from 'libs/events'
import {
  getMarketingRows,
  getFreeTrialByToken,
  getCourseSamples,
  getDefaultProduct,
  getV2Categories,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { useSprig } from 'libs/sprig'
import { getSSRQuizOnlyOnceCookie } from 'libs/utils/quizOnlyOnce'
import { trackServerSideEV } from 'libs/utils/trackServerSideEV'
import {
  CATEGORIES_TYPE,
  COURSES_TYPE,
  FREE_TRIAL_TYPE,
  MARKETING_ROWS_DATA_TYPE,
  PRODUCT_PLAN_TYPE,
} from 'types'
import { HeroQuiz } from './index/components/HeroQuiz'
import Meta from './index/components/Meta'

const BelowTheFold = dynamic(() => import('./index/components/BelowTheFold'), {
  ssr: false,
})

type HomeProps = Extract<
  InferGetServerSidePropsType<typeof getServerSideProps>,
  { status: 'success' }
>

const Home = ({
  annualPass,
  categories,
  trial,
  marketingRows,
  sampleCourses,
  ctaNextPage,
  carouselSpringboardItems,
  quizEntryModalFeaturedClasses,
}: HomeProps) => {
  useEffect(() => {
    if (annualPass) {
      trackProductViewed(annualPass)
    }
  }, [annualPass])

  useEffect(() => {
    trackPage('Home', {
      saved_completed_quiz: !!ctaNextPage,
      platform: 'next',
    })
  }, [ctaNextPage])

  useParsePromoFeatureVariables()
  useSprig()
  useTrackCarouselSpringboardExperiment()

  return (
    <StaticDataProvider
      value={{
        annualPass,
        categories,
        trial,
        marketingRows,
        sampleCourses,
        ctaNextPage,
        carouselSpringboardItems,
        quizEntryModalFeaturedClasses,
      }}
    >
      <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-home' }}>
        <TrialNoLongerAvailableModal />
        <PromoExplainerModal pageSource={PAGE_SOURCES.HOMEPAGE} />
        <BogoPromoModal />
        <QuizEntryModalLoader />
        <ProfitWell />
        <Meta />
        <BogoBanner className='mc-mb-2 mc-mb-md-0' />
        <EnterpriseAnnouncementBanner />
        <GoogleOneTap />

        {/* pen-5690_v1_hp_innovative_swing */}
        <HeroQuizExpWrapper Control={<HeroQuiz />} />
        <BelowTheFoldExpWrapper Control={<BelowTheFold />} />
        <StickyFooterExpWrapper />
        {/* pen-5690_v1_hp_innovative_swing */}
      </TrackingParamsProvider>
    </StaticDataProvider>
  )
}

Home.propTypes = {
  annualPass: PRODUCT_PLAN_TYPE,
  categories: CATEGORIES_TYPE,
  trial: FREE_TRIAL_TYPE,
  marketingRows: MARKETING_ROWS_DATA_TYPE,
  sampleCourses: COURSES_TYPE,
  ctaNextPage: PropTypes.string,
  quizSavedFlowClassResultCount: PropTypes.number,
  carouselSpringboardItems: PropTypes.array,
  quizEntryModalFeaturedClasses: PropTypes.array,
}

Home.pageSettings = {
  title: 'MasterClass - Home',
  showPromo: true,
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    req: {
      cookies: { referral_token: referralToken },
    },
  } = ctx

  const [
    ctaNextPage,
    carouselSpringboardBucket,
    HPInnovativeSwingBucket,
    quizEntryExperimentBucket,
  ] = await Promise.all([
    getSSRQuizOnlyOnceCookie(ctx),
    getBucket(ctx, CAROUSEL_SPRINGBOARD_EXP_FLAG),
    getBucket(ctx, HP_INNOVATIVE_SWING_FLAG_NAME),
    getBucket(ctx, QUIZ_ENTRY_IN_STICKY_FOOTER_EXP_FLAG),
  ])

  await trackServerSideEV(ctx, HP_INNOVATIVE_SWING_FLAG_NAME)

  // sample courses and carousel springboard items are not needed for HP innovative swing variant
  const isHPInnovativeSwingVariant =
    HPInnovativeSwingBucket?.startsWith('variant')

  const shouldFetchCarouselSpringboardData =
    carouselSpringboardBucket?.startsWith('variant')

  const shouldFetchQuizEntryClasses =
    quizEntryExperimentBucket?.startsWith('variant')

  return getPageData({
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      categories: getV2Categories(ctx),
      trial: getFreeTrialByToken(ctx, { referralToken }),
      marketingRows: getMarketingRows(ctx, {
        include: ['marketing_section_items', 'marketing_section_action'],
      }),
      sampleCourses: !isHPInnovativeSwingVariant
        ? getCourseSamples(ctx, {
            allowAltSet: true,
          })
        : Promise.resolve([]),
      carouselSpringboardItems:
        !isHPInnovativeSwingVariant && shouldFetchCarouselSpringboardData
          ? getCarouselSpringboardItems(ctx)
          : Promise.resolve([]),
      quizEntryModalFeaturedClasses: shouldFetchQuizEntryClasses
        ? getModalFeaturedClasses(ctx)
        : Promise.resolve([]),
    },
    propsMapper: (props) => ({
      ...props,
      ctaNextPage,
    }),
  })
}

export default Home
